import { useCallback, useMemo, useRef, useState } from 'react'
import { ConnectError } from '@connectrpc/connect'
import type { GenerateReviewResult } from '@customer/api/reviewApi'
import { reviewClient } from '@customer/api/reviewApi'
import { useMutation } from '@tanstack/react-query'

import { REASON_MAX, WORK_TYPE_MAX } from '@modules/review/surveyOptions'

const DETAIL_MAX = 1000
const CITY_MAX = 255

/** 施主が自由入力する文字列フィールド（汎用 setter `setField` の対象） */
type TextFieldKey = 'prefecture' | 'city' | 'motivation' | 'highlight' | 'ageGroup' | 'gender'

/** フィールドごとの文字数上限（未指定は無制限） */
const TEXT_FIELD_MAX: Partial<Record<TextFieldKey, number>> = {
  city: CITY_MAX,
  motivation: DETAIL_MAX,
  highlight: DETAIL_MAX,
}

export interface ReviewFormState {
  prefecture: string
  city: string
  workTypes: string[]
  reasons: string[]
  impressions: string[]
  motivation: string
  highlight: string
  ageGroup: string
  gender: string
  permissionToPost: boolean
}

const INITIAL_STATE: ReviewFormState = {
  prefecture: '',
  city: '',
  workTypes: [],
  reasons: [],
  impressions: [],
  motivation: '',
  highlight: '',
  ageGroup: '',
  gender: '',
  permissionToPost: true,
}

interface GenerateParams {
  companySlug: string
  dealId?: bigint
  captchaToken: string
}

/**
 * 口コミアンケートのフォーム状態と生成 mutation を管理するフック。
 *
 * customer アプリには Toaster が無いため、エラーは画面表示用の `errorMessage`
 * （ユーザー向け文言）に格納する。catch では console.error を出すが、
 * free_comment 等の PII は決してログに含めない。
 */
export const useReviewForm = () => {
  const [state, setState] = useState<ReviewFormState>(INITIAL_STATE)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // 送信中フラグ。state（mutation.isPending）は再描画まで反映されないため、同じ tick に
  // 積まれた2回目以降のクリックを止められない。ref は即座に書き換わるので、2回目以降を
  // 送信せずに捨てられる（STG 2026-09-25 に口コミ生成へ同時送信が約100件ずつ届いた件の再発防止）。
  const inFlightRef = useRef(false)

  const toggleSelection = useCallback(
    (section: 'workTypes' | 'reasons' | 'impressions', option: string, max?: number) => {
      setState(prev => {
        const current = prev[section]
        const isSelected = current.includes(option)
        if (isSelected) {
          return { ...prev, [section]: current.filter(o => o !== option) }
        }
        // 上限到達時は追加しない（呼び出し側でも disable するが二重ガード）
        if (max !== undefined && current.length >= max) return prev
        return { ...prev, [section]: [...current, option] }
      })
    },
    [],
  )

  // 自由入力の文字列フィールドは汎用 setter に集約（clamp はここで閉じる）。
  const setField = useCallback((key: TextFieldKey, value: string) => {
    const max = TEXT_FIELD_MAX[key]
    setState(prev => ({ ...prev, [key]: max ? value.slice(0, max) : value }))
  }, [])

  const setPermissionToPost = useCallback((value: boolean) => {
    setState(prev => ({ ...prev, permissionToPost: value }))
  }, [])

  const reset = useCallback(() => {
    setState(INITIAL_STATE)
    setErrorMessage(null)
  }, [])

  // 必須項目: 工事内容/理由/印象がそれぞれ 1 件以上（工事エリアは任意）
  const isValid = useMemo(
    () => state.workTypes.length >= 1 && state.reasons.length >= 1 && state.impressions.length >= 1,
    [state.workTypes.length, state.reasons.length, state.impressions.length],
  )

  const mutation = useMutation<GenerateReviewResult, unknown, GenerateParams>({
    mutationFn: async ({ companySlug, dealId, captchaToken }) =>
      await reviewClient.generateReview({
        companySlug,
        dealId,
        prefecture: state.prefecture,
        city: state.city,
        workTypes: state.workTypes,
        reasons: state.reasons,
        impressions: state.impressions,
        motivation: state.motivation,
        highlight: state.highlight,
        ageGroup: state.ageGroup,
        gender: state.gender,
        permissionToPost: state.permissionToPost,
        captchaToken,
      }),
  })

  const generate = useCallback(
    async (params: GenerateParams): Promise<GenerateReviewResult | undefined> => {
      if (inFlightRef.current) return undefined
      inFlightRef.current = true
      setErrorMessage(null)
      try {
        return await mutation.mutateAsync(params)
      } catch (error) {
        // PII（free_comment 等）はログに出さない。状態のみを残す。
        console.error('[ReviewForm] 口コミ生成に失敗しました', {
          isConnectError: error instanceof ConnectError,
        })
        setErrorMessage('口コミ文の作成に失敗しました。時間をおいて再度お試しください。')
        return undefined
      } finally {
        inFlightRef.current = false
      }
    },
    [mutation],
  )

  return {
    state,
    toggleSelection,
    setField,
    setPermissionToPost,
    reset,
    isValid,
    generate,
    isGenerating: mutation.isPending,
    errorMessage,
    constants: { WORK_TYPE_MAX, REASON_MAX, DETAIL_MAX, CITY_MAX },
  }
}
